import { useCallback } from 'react';
import { create } from 'zustand';
import { Languages } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AR } from './i18n-ar';

export type Lang = 'en' | 'ar';
const KEY = 'dovo.lang';

/** A saved choice; English until someone picks Arabic in the header. */
function initialLanguage(): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'ar' || saved === 'en') return saved;
  } catch {
    // Storage can be blocked; English it is.
  }
  return 'en';
}

/** Sets the page language and direction, which every logical class follows. */
export function applyLanguage(lang: Lang) {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
}

export const useLanguage = create<{ lang: Lang; setLang: (lang: Lang) => void }>((set) => ({
  lang: initialLanguage(),
  setLang: (lang) => {
    try {
      localStorage.setItem(KEY, lang);
    } catch {
      // Not remembered, but still applied for this visit.
    }
    applyLanguage(lang);
    set({ lang });
  },
}));

/** Arabic for an English interface string; English when there is none yet. */
export function translate(text: string, lang: Lang, vars?: Record<string, string | number>): string {
  let out = lang === 'ar' ? (AR[text] ?? text) : text;
  if (vars) for (const [key, value] of Object.entries(vars)) out = out.replace(`{${key}}`, String(value));
  return out;
}

export function useT() {
  const lang = useLanguage((state) => state.lang);
  return useCallback((text: string, vars?: Record<string, string | number>) => translate(text, lang, vars), [lang]);
}

/** English ⇄ العربية, for the header and the public pages. */
export function LanguageToggle({ className, tone = 'default' }: { className?: string; tone?: 'default' | 'dark' }) {
  const { lang, setLang } = useLanguage();
  return (
    <button
      type="button"
      onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors',
        tone === 'dark' ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        className,
      )}
      title={lang === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
    >
      <Languages className="h-4 w-4" />
      {lang === 'ar' ? 'English' : 'العربية'}
    </button>
  );
}
